import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { exportToExcel as exportToExcelAPI, importFromExcel } from '../services/api';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { Role } from '@redpanda/types';
import { Label, AlertModal, LoadingButton } from '@redpanda/ui';
import { notifyDataChanged } from '../utils/dataEvents';
import { useAccountStoreBase } from '../stores/accountStore';
import { useJournalStoreBase } from '../stores/journalStore';
import { useInvoiceStoreBase, useArchivedInvoiceStoreBase } from '../stores/invoiceStore';

/** 資料匯入匯出（照 CRM DataIOPage；路由限 SUPER_ADMIN）：會計科目、記帳分錄、發票；匯入的分錄一律是草稿 */
const DataIOPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userContext = useContext(UserContext);
  const userRole = userContext?.user?.role;
  const navigate = useNavigate();
  
  // Export states
  const [isExcelExporting, setIsExcelExporting] = useState(false);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [exportProgress, setExportProgress] = useState(0);
  const exportableSheets = ['accounts', 'journals', 'invoices', 'bankAccounts', 'bankTransactions'];

  // Import states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingServer, setIsProcessingServer] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Alert dialog state
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alertDialogTitle, setAlertDialogTitle] = useState('');
  const [alertDialogMessage, setAlertDialogMessage] = useState('');

  useEffect(() => {
    if (userRole !== Role.SUPER_ADMIN) {
      navigate('/');
    }
  }, [userRole, navigate]);

  const handleSheetSelection = (sheet: string) => {
    setSelectedSheets(prev => 
      prev.includes(sheet) ? prev.filter(s => s !== sheet) : [...prev, sheet]
    );
  };

  const handleSelectAll = () => {
    setSelectedSheets(exportableSheets);
  };

  const handleDeselectAll = () => {
    setSelectedSheets([]);
  };

  const handleExportExcel = async () => {
    if (selectedSheets.length === 0) {
        setAlertDialogTitle(t('common.messages.operationFailed'));
        setAlertDialogMessage(t('dataIO:export.selectSheetsError'));
        setShowAlertDialog(true);
        return;
    }
    setIsExcelExporting(true);
    setExportProgress(0);
    
    // 簡單的進度模擬
    const progressInterval = setInterval(() => {
      setExportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);
    
    try {
        const response = await exportToExcelAPI(selectedSheets, i18n.language);
        setExportProgress(100);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'export.xlsx';
        if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename\*?=UTF-8''(.+)/);
            if (fileNameMatch?.[1]) {
                fileName = decodeURIComponent(fileNameMatch[1]);
            } else {
                const regularFileNameMatch = contentDisposition.match(/filename="(.+)"/);
                if(regularFileNameMatch?.[1]) fileName = regularFileNameMatch[1];
            }
        }
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Failed to export to Excel:', error);
        setAlertDialogTitle(t('common.messages.operationFailed'));
        setAlertDialogMessage(t('dataIO:export.failed'));
        setShowAlertDialog(true);
    } finally {
        clearInterval(progressInterval);
        setIsExcelExporting(false);
        setTimeout(() => setExportProgress(0), 1000);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith('.xlsx')) {
      setSelectedFile(file);
      setImportResult(null);
    } else {
      setSelectedFile(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setAlertDialogTitle(t('common.messages.operationFailed'));
      setAlertDialogMessage(t('dataIO:import.selectFileError'));
      setShowAlertDialog(true);
      return;
    }
    setIsImporting(true);
    setUploadProgress(0);
    setIsProcessingServer(false);
    setImportResult(null);
    setImportProgress(0);

    // 進度模擬：平滑增長到 90%，避免卡住感
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 8;
      });
    }, 400);

    try {
      const onUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        if (percentCompleted === 100) {
          setIsProcessingServer(true);
        }
      };

      const response = await importFromExcel(selectedFile, i18n.language, onUploadProgress);
      setImportProgress(100);
      // 後端回傳的 message 是翻譯 key（import_excel_success），標題用 dataIO 的翻譯
      setImportResult({
        message: t('dataIO:import.resultTitle.success'),
        report: response.data.report,
        success: true
      });

      // 匯入會動到所有資料：各列表快取清掉，儀表板、動態重新載入
      [useAccountStoreBase, useJournalStoreBase, useInvoiceStoreBase, useArchivedInvoiceStoreBase]
        .forEach(store => store.getState().clearCache());
      notifyDataChanged('accounts');
      notifyDataChanged('journals');
      notifyDataChanged('invoices');
      notifyDataChanged('bankAccounts');
      notifyDataChanged('bankTransactions');

    } catch (error: any) {
      console.error('Failed to import file:', error);
      // 後端錯誤是翻譯 key（import_excel_error）或逾時說明；key 一律顯示通用的匯入失敗訊息
      const serverError: string | undefined = error.response?.data?.error;
      const errorMessage = serverError && !serverError.startsWith('import_') ? serverError : 'dataIO:import.failed';
      setImportResult({
        message: t('dataIO:import.resultTitle.failed'),
        error: errorMessage,
        report: {}
      });
    } finally {
      clearInterval(progressInterval);
      setIsImporting(false);
      setIsProcessingServer(false);
      setSelectedFile(null);
      setUploadProgress(0);
      setTimeout(() => setImportProgress(0), 1000);
      if(fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCloseAlertDialog = () => {
    setShowAlertDialog(false);
    setAlertDialogTitle('');
    setAlertDialogMessage('');
  };

  if (userRole !== Role.SUPER_ADMIN) return null;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">{t('dataIO:title')}</h1>
      
      {/* 本月發票數量（超過上限時提示；匯入的發票同樣計入） */}
      <InvoiceLimitBanner />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Export Card */}
        <div className="bg-surface border border-line rounded-lg p-6 space-y-6">
          <div>
            <h3 className="text-base font-semibold text-fg mb-1">{t('dataIO:export.title')}</h3>
            <p className="text-sm text-fg-secondary mb-4">{t('dataIO:export.description')}</p>
            <div className="space-y-2 mb-4">
                {exportableSheets.map(sheet => (
                    <Label key={sheet} className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={selectedSheets.includes(sheet)} onChange={() => handleSheetSelection(sheet)} className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"/>
                        <span>{t(`translation:components.dataIO.sheets.${sheet}`)}</span>
                    </Label>
                ))}
            </div>
            {/* 全選／全部取消是輔助動作：文字按鈕，整個區塊只留「匯出」一顆主要按鈕（§3-5） */}
            <div className="flex items-center gap-4 mb-4 text-sm">
                <button type="button" onClick={handleSelectAll} className="font-medium text-accent hover:underline">
                  {t('dataIO:export.selectAll')}
                </button>
                <button type="button" onClick={handleDeselectAll} className="font-medium text-fg-secondary hover:text-fg transition-colors">
                  {t('dataIO:export.deselectAll')}
                </button>
            </div>
            <LoadingButton
              type="button"
              onClick={handleExportExcel}
              loading={isExcelExporting}
              disabled={selectedSheets.length === 0}
              customText={{
                create: t('dataIO:export.button'),
                createLoading: t('common.status.exporting')
              }}
            />
          </div>

          {/* Export Progress Bar */}
          {isExcelExporting && exportProgress > 0 && (
            <div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 dark:bg-slate-800">
                <div 
                  className="bg-accent h-1.5 rounded-full"
                  style={{ width: `${Math.round(exportProgress)}%` }}
                ></div>
              </div>
              <div className="text-[13px] text-fg-secondary tabular-nums mt-1">
                {t('dataIO:export.progress', { percent: Math.round(exportProgress) })}
              </div>
            </div>
          )}
        </div>

        {/* Import Card */}
        <div className="bg-surface border border-line rounded-lg p-6">
          <h3 className="text-base font-semibold text-fg mb-1">{t('dataIO:import.title')}</h3>
          <p className="text-sm text-fg-secondary mb-4">{t('dataIO:import.description')}</p>
          <div className="flex items-stretch gap-3">
            <Label
              className="inline-flex min-w-0 items-center justify-center px-4 rounded-md border border-slate-300 dark:border-slate-600 h-10 text-sm font-medium text-fg transition-colors cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <svg className="w-4 h-4 mr-2 flex-shrink-0 text-fg-secondary" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4 4-4-4h3V7h2v4z" />
              </svg>
              <span className="truncate">{selectedFile ? selectedFile.name : t('dataIO:import.selectFile')}</span>
              <input
                ref={fileInputRef}
                type='file'
                className="hidden"
                accept=".xlsx"
                onChange={handleFileChange}
              />
            </Label>
            <LoadingButton
              type="button"
              onClick={handleImport}
              loading={isImporting}
              disabled={!selectedFile}
              className="whitespace-nowrap"
              customText={{
                create: t('dataIO:import.button'),
                createLoading: t('common.status.importing')
              }}
            />
          </div>
          {/* Import Progress Bar */}
          {isImporting && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {uploadProgress < 100 ? t('dataIO:import.uploadProgress', { percent: uploadProgress }) :
                 isProcessingServer ? t('dataIO:import.serverProcessing') :
                 t('dataIO:import.progressComplete', { percent: Math.round(importProgress) })}
              </p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 dark:bg-slate-800 mt-1">
                <div className="bg-accent h-1.5 rounded-full" style={{ width: `${Math.round(importProgress)}%` }}></div>
              </div>
            </div>
          )}
          {importResult && (
            <div className="mt-4 pt-4 border-t border-line space-y-3">
              <h4 className="text-sm font-semibold text-fg">{importResult.message}</h4>
              {importResult.report && Object.keys(importResult.report).length > 0 ? (
                Object.entries(importResult.report).map(([sheetName, result]: [string, any]) => (
                  <div key={sheetName} className="text-sm">
                    <p className="font-medium text-fg">{sheetName}</p>
                    {/* 結果一行文字，數字不上色；只有失敗數 > 0 才用紅色（§3-2） */}
                    <div className="flex gap-4 mt-0.5 text-[13px] text-fg-secondary tabular-nums">
                        <span>{t('dataIO:import.stats.created')} <span className="text-fg">{result.created}</span></span>
                        <span>{t('dataIO:import.stats.updated')} <span className="text-fg">{result.updated}</span></span>
                        <span className={result.errors.length > 0 ? 'text-red-600 dark:text-red-400' : ''}>{t('dataIO:import.stats.failed')} {result.errors.length}</span>
                    </div>
                    {result.errors.length > 0 && (
                      <div className="mt-2">
                        <details>
                          <summary className="text-[13px] text-fg-secondary cursor-pointer">{t('dataIO:import.viewErrors')}</summary>
                          <ul className="list-disc list-inside text-[13px] text-red-600 dark:text-red-400 max-h-24 overflow-y-auto mt-1">
                            {result.errors.map((err: any, index: number) => {
                              const [key, paramsJson] = err.message.split('|');
                              const params = paramsJson ? JSON.parse(paramsJson) : {};
                              return (
                                <li key={index}>{t('dataIO:import.errorRow', { row: err.row, message: t(key, params) })}</li>
                              )
                            })}
                          </ul>
                        </details>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-red-600 dark:text-red-400">{t(importResult.error)}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <AlertModal isOpen={showAlertDialog} onClose={handleCloseAlertDialog} title={alertDialogTitle} message={alertDialogMessage} confirmText={t('common.actions.ok')} />
    </div>
  );
};

export default DataIOPage;